/**
 * Pillar 6 — Superintendent Command View.
 *
 * Single page. Super opens this and sees, at a glance, every operational
 * loop that has not closed:
 *
 *   - Unread       — foreman hasn't even opened the item
 *   - Unacknowledged — foreman opened, not acted
 *   - Questions    — clarifications waiting on the super
 *   - Conflicts    — field-conflicts waiting on the super
 *   - Overdue      — anything > 24h old still open
 *   - Complete     — closure trail (last 7 days)
 *
 * Each bucket is a section with the same row UI from the Foreman Inbox so
 * the super can click straight through to the item and resolve it.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Mail, Eye, MessageSquareWarning, ShieldAlert,
  Clock, CheckCircle2, ChevronRight, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import StatusPill from "@/components/inbox/StatusPill";
import ImpactChip from "@/components/war_room/ImpactChip";
import StoryPanels from "@/components/stories/StoryPanels";


const BUCKETS = [
  { id: "unread",         label: "Unread",         icon: Mail,                 tone: "warning" },
  { id: "unacknowledged", label: "Unacknowledged", icon: Eye,                  tone: "warning" },
  { id: "questions",      label: "Questions",      icon: MessageSquareWarning, tone: "warning" },
  { id: "conflicts",      label: "Field Conflicts",icon: ShieldAlert,          tone: "danger"  },
  { id: "overdue",        label: "Overdue",        icon: Clock,                tone: "danger"  },
  { id: "complete",       label: "Complete (7 days)", icon: CheckCircle2,      tone: "muted"   },
];


export default function SuperintendentCommandView() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const projectId = params.get("project_id") || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("unread");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const url = projectId
      ? `/superintendent/command-view?project_id=${projectId}`
      : "/superintendent/command-view";
    api.get(url).then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  if (loading && !data) {
    return (
      <div className="p-8 text-sm text-[var(--fop-text-3)] flex items-center gap-2" data-testid="command-view-loading">
        <Loader2 className="animate-spin" size={14} /> Loading the command view…
      </div>
    );
  }

  const counts = data?.counts || {};
  const rows = data?.[tab] || [];

  return (
    <div className="min-h-screen bg-[var(--fop-bg)]" data-testid="command-view">
      <header className="border-b border-[var(--fop-border)] bg-[var(--fop-surface)] px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
          aria-label="Back"
          data-testid="command-view-back"
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
            Superintendent Command View
          </div>
          <div className="text-sm font-semibold text-[var(--fop-text)]">
            One glance · everything actionable
          </div>
        </div>
      </header>

      <div className="p-4 max-w-6xl mx-auto">
        {/* Story Panels — the operational source of truth, surfaced first. */}
        {projectId && (
          <div className="mb-4" data-testid="command-view-stories">
            <StoryPanels projectId={projectId} testidPrefix="command-view-stories-panel" />
          </div>
        )}

        {/* Bucket tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4" data-testid="command-view-buckets">
          {BUCKETS.map(({ id, label, icon: Icon, tone }) => {
            const n = counts[id] || 0;
            const active = tab === id;
            const toneStyle = tone === "danger"
              ? "border-[var(--fop-danger,#dc2626)]/60"
              : tone === "warning"
                ? "border-[var(--fop-warning)]/55"
                : "border-[var(--fop-border)]";
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`command-view-bucket-${id}`}
                data-active={active ? "1" : "0"}
                className={`p-3 rounded border ${toneStyle} text-left transition-colors ${
                  active ? "bg-[var(--fop-primary-soft)]" : "bg-[var(--fop-surface)] hover:bg-[var(--fop-surface-2)]"
                }`}
              >
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                  <Icon size={12} /> {label}
                </div>
                <div className="text-xl font-semibold text-[var(--fop-text)] mt-1" data-testid={`command-view-count-${id}`}>{n}</div>
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden" data-testid="command-view-list">
          {rows.length === 0 ? (
            <div className="p-8 text-center" data-testid="command-view-empty">
              <CheckCircle2 size={28} className="mx-auto text-[var(--fop-success)]" />
              <div className="mt-2 font-semibold text-[var(--fop-text)]">All clear in this bucket</div>
            </div>
          ) : (
            rows.map((it) => (
              <Link
                key={it.id}
                to={`/inbox?id=${it.id}`}
                className="flex items-start gap-3 px-3 py-2.5 border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)] transition-colors"
                data-testid={`command-view-row-${it.id}`}
              >
                <StatusPill status={it.status} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13px] text-[var(--fop-text)] truncate">
                    {it.title || `${it.sheet_number} REV ${it.current_revision}`}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
                    {it.sheet_number} · REV {it.current_revision} · {it.assignee_name}
                  </div>
                </div>
                <ImpactChip severity={it.priority || "moderate"} />
                <ChevronRight size={12} className="text-[var(--fop-text-3)] mt-1" />
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
